import React, { useState, useEffect } from 'react';
import { Bell, Menu, X, User, LogOut, Settings, GraduationCap, AlignJustify } from 'lucide-react';
import ProfileSlideOver from '../ProfileSlideOver';
import UserServices from '../../services/UserServices';

function ParentHeader({ notifications = [], parentName = "Parent", onMenuClick, setActiveTab, onLogout, profileImage = null, onProfileImageUpdate = null }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [parentImage, setParentImage] = useState(profileImage);
  const [name, setName] = useState(parentName);

  useEffect(() => {
    const savedName = localStorage.getItem('userName');
    const savedImage = localStorage.getItem('profileImage');
    const savedId = localStorage.getItem('userId');
    if (savedName) setName(savedName);
    if (savedImage) setParentImage(savedImage);

    if (savedId) {
      UserServices.getUserById(savedId)
        .then(res => {
          const d = res.data && res.data.ResultSet ? res.data.ResultSet[0] : null;
          if (d) {
            if (d.UserName) setName(d.UserName);
            if (d.ProfileImage) {
              setParentImage(d.ProfileImage);
              localStorage.setItem('profileImage', d.ProfileImage);
            }
            if (d.UserID) localStorage.setItem('userId', d.UserID);
            if (d.UserName) localStorage.setItem('userName', d.UserName);
          }
        })
        .catch(() => {});
    }
  }, []);

  return (
    <header className="bg-gradient-to-r from-[#1E3A5F] via-[#3B6FB6] to-[#1E3A5F] text-white shadow-xl">
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#F5C518] via-[#FFE066] to-[#F5C518]"></div>
      
      {/* Desktop & Tablet Header */}
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Sidebar Toggle + Logo & Brand */}
          <div className="flex items-center gap-3">
            {/* Sidebar Toggle Button */}
            <button
              onClick={onMenuClick}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors border border-white/20 group"
              title="Open Menu"
            >
              <AlignJustify className="w-5 h-5 group-hover:text-[#FFE066] transition-colors" />
            </button>
            
            <div className="bg-[#F5C518] rounded-lg p-2 shadow-md">
              <GraduationCap className="w-6 h-6 text-[#1E3A5F]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">TrackMate</h1>
              <p className="text-xs sm:text-sm text-[#FFE066]">Parent Portal</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button onClick={() => setActiveTab('dashboard')} className="text-white/90 hover:text-[#FFE066] transition-colors font-medium border-b-2 border-transparent hover:border-[#F5C518] pb-1">Dashboard</button>
            <button onClick={() => setActiveTab('my-child')} className="text-white/90 hover:text-[#FFE066] transition-colors font-medium border-b-2 border-transparent hover:border-[#F5C518] pb-1">My Child</button>
            <button onClick={() => setActiveTab('history')} className="text-white/90 hover:text-[#FFE066] transition-colors font-medium border-b-2 border-transparent hover:border-[#F5C518] pb-1">History</button>
            <button onClick={() => setActiveTab('alerts')} className="text-white/90 hover:text-[#FFE066] transition-colors font-medium border-b-2 border-transparent hover:border-[#F5C518] pb-1">Alerts</button>
          </nav>

          {/* Right Section - Desktop */}
          <div className="hidden md:flex items-center gap-4">
            {/* Notifications */}
            <div className="relative">
              <button className="relative p-2 rounded-lg hover:bg-white/10 transition-colors border border-white/20">
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F5C518] text-[#1E3A5F] text-xs rounded-full flex items-center justify-center font-bold shadow-lg">
                    {notifications.length > 9 ? '9+' : notifications.length}
                  </span>
                )}
              </button>
            </div>

            {/* Profile Menu */}
            <div className="relative">
              <button
                onClick={() => setProfileOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors border border-white/20 group"
              >
                {parentImage ? (
                  <img 
                    src={parentImage} 
                    alt={name}
                    className="w-8 h-8 rounded-full object-cover group-hover:ring-2 ring-[#F5C518] transition-all"
                  />
                ) : (
                  <div className="w-8 h-8 bg-[#F5C518] rounded-full flex items-center justify-center group-hover:ring-2 ring-[#F5C518] transition-all">
                    <User className="w-5 h-5 text-[#1E3A5F]" />
                  </div>
                )}
                <span className="text-sm font-medium">{name || 'Profile'}</span>
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors border border-white/20"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#1E3A5F] px-4 py-4 space-y-2 border-t border-[#3B6FB6]">
          <button onClick={() => { setActiveTab('dashboard'); setMobileMenuOpen(false); }} className="w-full text-left block px-4 py-3 rounded-lg hover:bg-[#3B6FB6] transition-colors font-medium">Dashboard</button>
          <button onClick={() => { setActiveTab('my-child'); setMobileMenuOpen(false); }} className="w-full text-left block px-4 py-3 rounded-lg hover:bg-[#3B6FB6] transition-colors font-medium">My Child</button>
          <button onClick={() => { setActiveTab('history'); setMobileMenuOpen(false); }} className="w-full text-left block px-4 py-3 rounded-lg hover:bg-[#3B6FB6] transition-colors font-medium">History</button>
          <button onClick={() => { setActiveTab('alerts'); setMobileMenuOpen(false); }} className="w-full text-left block px-4 py-3 rounded-lg hover:bg-[#3B6FB6] transition-colors font-medium">Alerts</button>
          
          <div className="border-t border-[#3B6FB6] pt-4 mt-4">
            <button onClick={() => { setActiveTab('my-child'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-2 px-4 py-3 rounded-lg hover:bg-[#3B6FB6] transition-colors font-medium">
              <User className="w-5 h-5 text-[#FFE066]" />
              <span>My Profile</span>
            </button>
            <button onClick={() => { setActiveTab('settings'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-2 px-4 py-3 rounded-lg hover:bg-[#3B6FB6] transition-colors font-medium">
              <Settings className="w-5 h-5 text-[#FFE066]" />
              <span>Settings</span>
            </button>
            <button onClick={() => { onLogout && onLogout(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-2 px-4 py-3 rounded-lg hover:bg-red-900/30 transition-colors font-medium text-red-300">
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Profile Slide-Over */}
      <ProfileSlideOver
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
        user={{ name: name, role: 'Parent', profileImage: parentImage }}
        onSettings={() => { setActiveTab('settings'); setProfileOpen(false); }}
        onLogout={() => { onLogout && onLogout(); setProfileOpen(false); }}
        onImageUpdate={(image) => {
          setParentImage(image);
          localStorage.setItem('profileImage', image);
          if (onProfileImageUpdate) onProfileImageUpdate(image);
        }}
      />
        onLogout={() => { onLogout && onLogout(); setProfileOpen(false); }}
      />
    </header>
  );
}

export default ParentHeader;
